// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useRef, useCallback } from 'react';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import { ReactComponent as SearchIcon } from '../../assets/Icons/lucideIcons/search.svg';
import useGlobalLoader from '../../hooks/useGlobalLoader';
import {
  TOOLBAR_ICON_LEFT_PADDING,
  TOOLBAR_ICON_TEXT_GAP,
  TOOLBAR_CONTROL_HEIGHT,
} from '../../const/tableToolbarStyles';

const CustomTextField = styled(TextField)(({ width }) => ({
  padding: '0px',
  marginTop: '0px',
  '& .MuiInputBase-root': {
    height: TOOLBAR_CONTROL_HEIGHT,
    paddingLeft: TOOLBAR_ICON_LEFT_PADDING,
    gap: TOOLBAR_ICON_TEXT_GAP,
    display: 'flex',
    alignItems: 'center',
  },
  '& input.MuiInputBase-input': {
    padding: '0 !important',
  },
  // Remove extra adornment margin so the gap token controls spacing
  '& .MuiInputAdornment-root': {
    marginRight: 0,
  },
  // Target the direct parent if needed
  '&&': {
    width: width || '200px', // Use dynamic width or fallback to default
    maxWidth: width || '200px',
    minWidth: width || '200px',
  },
  variants: [
    {
      props: ({ borderColor }) => Boolean(borderColor),
      style: ({ borderColor }) => ({
        '& .MuiOutlinedInput-root': {
          borderColor: `${borderColor} !important`,
        },
        '& .MuiOutlinedInput-root:hover': {
          borderColor: `${borderColor} !important`,
        },
        '& .MuiOutlinedInput-root.Mui-focused': {
          borderColor: `${borderColor} !important`,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: `${borderColor} !important`,
        },
      }),
    },
  ],
}));

const SearchTextField = ({ endIcon, borderColor, ...props }) => {
  const { isLoading } = useGlobalLoader();
  const liveRegionRef = useRef(null);

  /**
   * @function handleFocus
   * Handles focus event by setting the live region text to the aria-label when focused.
   */
  const handleFocus = useCallback(() => {
    if (liveRegionRef.current && props['aria-label']) {
      liveRegionRef.current.innerText = props['aria-label'];
    }
  }, [props['aria-label']]); // Dependencies: only changes when aria-label changes

  /**
   * @function handleBlur
   * Handles blur event by clearing the live region text when focus is lost.
   */
  const handleBlur = useCallback(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.innerText = '';
    }
  }, []); // No dependencies, runs only on blur

  // handleKeyDown and handlePaste specially added to stop user from typing if API calls are ongoing.
  const handleKeyDown = useCallback(
    (e) => {
      if (isLoading) {
        e?.preventDefault(); // block typing when loading
        return; // do not call consumer handler
      }
      // Call consumer-provided handler if it exists
      if (props?.onKeyDown) {
        props?.onKeyDown(e);
      }
    },
    [isLoading, props?.onKeyDown]
  );

  const handlePaste = useCallback(
    (e) => {
      if (isLoading) {
        e.preventDefault(); // block pasting when loading
        return; // do not call consumer handler
      }
      if (props?.onPaste) {
        props?.onPaste(e);
      }
    },
    [isLoading, props?.onPaste]
  );

  return (
    <>
      <CustomTextField
        borderColor={borderColor}
        slotProps={{
          input: {
            'aria-label': props['aria-label'], // Ensure aria-label is set
            'aria-describedby': props['aria-describedby'], // Provide additional context if needed
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" width="18px" height="18px" />
              </InputAdornment>
            ),
            ...(endIcon && {
              endAdornment: (
                <InputAdornment position="start">{endIcon}</InputAdornment>
              ),
            }),
          },
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        role="alert"
        aria-live="assertive"
        style={{
          width: '0px',
          height: '0px',
          overflow: 'hidden',
          position: 'absolute',
        }}
      />
    </>
  );
};

export default SearchTextField;
